import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { AlfrescoApiService, TranslationService } from '@alfresco/adf-core';
import { ReloadCustomListTableAction } from 'projects/alden-extension/src/lib/store/actions/alden.actions';
import { AppStore, getUserProfile } from '@alfresco/aca-shared/store';
import { Store } from '@ngrx/store';
import { FormGroup, Validators } from '@angular/forms';
import { UDeuStepFormField, UDeuStepForm, UDeuStep } from '../model/classes/udeu-step';
import { KeyValue } from '@angular/common';
import { SMTDataListsService } from 'projects/alden-extension/src/lib/services/data-lists.service';
import { EMPLEADO_DATALIST_NAME, SUBDIVISION_NOMINAL_FACULTADES_GAUDE_DATALIST_NAME, SUBDIVISION_NOMINAL_GAUDE_DATALIST_NAME } from '../model/contants/datalists';
import { GaudeSubdivisionNominal } from '../model/classes/gaude-subdivision-nominal';
import { MinimalNodeEntity, NodePaging, PeopleApi, Person } from '@alfresco/js-api';
import { UdeuStatus } from '../model/classes/udeu-status';
import { ContentManagementService } from 'app/src/app/services/content-management.service';
import { ProfileState } from '@alfresco/adf-extensions';
import { Observable } from 'rxjs';
import { Empleado, GaudeEmpleado } from '../model/classes/gaude-empleado';
import { SYS_NODE_UUID_PROP, UDEU_BOUD_RESOLUTIONS_TYPE } from '../model/contants/deusto';
import { MatFormFieldType } from '../model/enums/mat-form-field-types';
import { UdeuStatusMultiple } from '../model/classes/udeu-status-multiple';
import { SMTConfirmDialogComponent } from 'projects/alden-extension/src/lib/dialogs/confirm/confirm.dialog';

@Injectable({
  providedIn: 'root'
})
export class UdeuService {
  personDetails: Person;

  profile$: Observable<ProfileState>;
  profile: ProfileState;

  constructor(private store: Store<AppStore>, public dialog: MatDialog, private alfrescoApiService: AlfrescoApiService, 
    private smtDataListsService: SMTDataListsService, private contentService: ContentManagementService, private translationService: TranslationService) { 
      this.profile$ = this.store.select(getUserProfile);
    }

  reloadCustomListTable() {
    setTimeout(() => {  // Estudiar appHookService.reload en el ReloadCustomListTable
      this.store.dispatch(new ReloadCustomListTableAction(false))
    }, 2000); // Espera de 2 segundos
  }

  deleteNode(items: MinimalNodeEntity[]) {
    const dialogRef = this.dialog.open(SMTConfirmDialogComponent, {
        data: {
          title: 'DEUSTO.DIALOGS.DELETE_ELEMENTS.TITLE',
          message: 'DEUSTO.DIALOGS.DELETE_ELEMENTS.MESSAGE',
          htmlContent: this.translationService.instant(items.length === 1 ? 'DEUSTO.DIALOGS.DELETE_ELEMENTS.ELEMENT_MESSAGE' : 'DEUSTO.DIALOGS.DELETE_ELEMENTS.MESSAGE', { number: items.length }),
          noLabel: 'DEUSTO.DIALOGS.DELETE_ELEMENTS.NO_LABEL',
          yesLabel: 'DEUSTO.DIALOGS.DELETE_ELEMENTS.YES_LABEL',
        },
        minWidth: '250px'
      });
  
      dialogRef.afterClosed().subscribe((result) => {
        if (result) { 
            this.contentService.deleteNodes(items);
            this.reloadCustomListTable();
        }
      });
  }

  getGaudeDataListValues(datalistName: string, value: string = "", year: string = ""): Promise<any> {
    const path = `smtecno/ssm/deusto/gaude/items?endpoint=${datalistName}&value=${value}&year=${year}`;
    const httpMethod = 'GET';
    const responseType = '';
    const url = 'alfresco/service';

    return this.alfrescoApiService
      .getInstance()
      .contentClient.callApi(
        path, httpMethod,
        {}, {}, {}, {}, {}, [], [],
        responseType, url
      );
  }
   
  geActiveProducingBodyListValues(): Promise<any> {
    const path = `smtecno/ssm/deusto/boud/producing`;
    const httpMethod = 'GET';
    const responseType = '';
    const url = 'alfresco/service';

    return this.alfrescoApiService
      .getInstance()
      .contentClient.callApi(
        path, httpMethod,
        {}, {}, {}, {}, {}, [], [],
        responseType, url
      );
  }

  getObjectValues(object: any): any[] {
    return Object.values(object);
  }

  getObjectKeys(formGroup: FormGroup<any>): string[] {
    return Object.keys(formGroup.controls);
  }

  isRequired(controlName: string, formGroup: FormGroup<any>): boolean {
    return formGroup.controls[controlName].hasValidator(Validators.required);
  }

  isInvalid(controlName: string, formGroup: FormGroup<any>): boolean {
    const control = formGroup.controls[controlName];
    return control.errors !== null && control.touched;
  }

  getMasterList(steps: { [id: string]: UDeuStep }, stepId: string, fieldName: string, dataListName: string, sortByKey: boolean = false): Promise<any[]> {
    return this.getListValues(steps, stepId, fieldName, dataListName, '', '', false, false, sortByKey);
  }

  getListWithMasterKey(steps: { [id: string]: UDeuStep }, stepId: string, fieldName: string, dataListName: string, parentKey?: string, masterKey?: string, reset?: boolean, needParentKey: boolean = true, sortByKey: boolean = false): Promise<any[]> { 
    return this.getListValues(steps, stepId, fieldName, dataListName, parentKey, masterKey, reset, needParentKey, sortByKey);
  }

  getList(steps: { [id: string]: UDeuStep }, stepId: string, fieldName: string, dataListName: string, parentKey?: string, reset?: boolean, needParentKey: boolean = true, sortByKey: boolean = false): Promise<any[]> {
    return this.getListValues(steps, stepId, fieldName, dataListName, parentKey, '', reset, needParentKey, sortByKey);
  }

  getListFromMasterKey(steps: { [id: string]: UDeuStep }, stepId: string, fieldName: string, dataListName: string, masterKey?: string, reset?: boolean, needParentKey: boolean = true, sortByKey: boolean = false, needMasterKey: boolean = true): Promise<any[]> {
    return this.getListValues(steps, stepId, fieldName, dataListName, '', masterKey, reset, needParentKey, sortByKey, needMasterKey);
  }

  getListValues(steps: { [id: string]: UDeuStep }, stepId: string, fieldName: string, dataListName: string, parentKey?: string, masterKey?: string, reset?: boolean, needParentKey: boolean = true, sortByKey: boolean = false, needMasterKey: boolean = false): Promise<any[]> {
    return new Promise<any[]>(async (resolve, reject) => {
      try {
        const form = steps[stepId].form;

        let options = [];
        if (needParentKey) {
          if (parentKey) {
            await this.smtDataListsService.getDataListValues(dataListName, parentKey, masterKey, sortByKey).then((res) => options = res);  
            this.markAsEmptyIfNecessary(fieldName, form, options);   
          }else if(needMasterKey && masterKey === UDEU_BOUD_RESOLUTIONS_TYPE){
             await this.smtDataListsService.getDataListValues(dataListName, '', masterKey, sortByKey).then((res) => options = res);
             this.markAsEmptyIfNecessary(fieldName, form, options) ;
          }else {         
            const optionsMaster = form.fields[fieldName].options;
            if(!reset && optionsMaster) {
              options = optionsMaster;
            }
          }
        }else {
          await this.smtDataListsService.getDataListValues(dataListName, '', masterKey, sortByKey).then((res) => options = res);
          this.markAsEmptyIfNecessary(fieldName, form, options);
        }

        form.fields[fieldName].options = options;
        this.setValueControl(form, fieldName, reset);
        resolve(options);
      } catch (error) {
        console.error('Error:', error);
        reject(error);
      }
    });
  }

  markAsEmptyIfNecessary(fieldName: string, form: UDeuStepForm, options: KeyValue<string, string>[]) {
    const optional = form.fields[fieldName].optional;
    if(!optional && (!options || options.length === 0)) {
      form.fields[fieldName].empty = true;
    } else {
      form.fields[fieldName].empty = false;
    }
  }

  getGaudeList(steps: { [id: string]: UDeuStep }, stepId: string, fieldName: string, dataListName: string, value?: string,reset?:boolean): Promise<any[]> {
    return new Promise<any[]>(async (resolve, reject) => {
      try {
        const FIELD_NAME = fieldName;
        const form = steps[stepId].form;

        let options = [];
        if(dataListName){
          await this.getGaudeDataListValues(dataListName, value).then((res) => {   
            switch (dataListName) {
              case SUBDIVISION_NOMINAL_GAUDE_DATALIST_NAME:
              case SUBDIVISION_NOMINAL_FACULTADES_GAUDE_DATALIST_NAME:
                options = (res as GaudeSubdivisionNominal).items.map((item) => { 
                  return { key: item.id_subdivision, value: item.subdivision_archivo }
                });
                break;
              default:
                options = res;
                break;
            }
          });
        }

        form.fields[FIELD_NAME].options = options;
        this.setValueControl(form, FIELD_NAME,reset);
        resolve(options);
      } catch (error) {
        console.error('Error:', error);
        reject(error);
      }
    });
  }

  getActiveProducingBodiesList(steps: { [id: string]: UDeuStep }, stepId: string, fieldName: string, reset?: boolean): Promise<any[]> {
    return new Promise<any[]>(async (resolve, reject) => {
      try {
        const FIELD_NAME = fieldName;
        const form = steps[stepId].form;

        let options = [];
      
        await this.geActiveProducingBodyListValues().then((res) => options = res);

        form.fields[FIELD_NAME].options = options;
        this.setValueControl(form, FIELD_NAME,reset);
        resolve(options);
      } catch (error) {
        console.error('Error:', error);
        reject(error);
      }
    });
  }

  formFieldChangeFunc(formField: UDeuStepFormField, option: KeyValue<string, string> | KeyValue<string, string>[]) { 
    if(formField.type === MatFormFieldType.SELECT_MULTIPLE) {
      formField.changeMultFunc?.forEach(func => func(option as KeyValue<string, string>[], true));
    } else {
      formField.changeFunc?.forEach(func => func(option as KeyValue<string, string>, true));
    }
  }

  async setValueControl(form: UDeuStepForm, controlName: string, reset: boolean = false) {
    const control = form.group.get(controlName);
    const formField = form.fields[controlName];
    let value = control.value;

    if (reset) {
      control.reset();
      value = null;
      await this.changeFunction(formField, value, reset);
    }

    if (!value && formField.options?.length === 1) {
      value = form.fields[controlName].type === MatFormFieldType.SELECT_MULTIPLE ? [formField.options[0]] : formField.options[0];
      control.setValue(value);
    }

    await this.changeFunction(formField, value, reset);
  }

  async changeFunction(formField: UDeuStepFormField, value: any, reset: boolean) {
    const funcs = formField.type === MatFormFieldType.SELECT_MULTIPLE
    ? formField.changeMultFunc ?? []
    : formField.changeFunc ?? [];

    // Mapea a promesas, si la función devuelve void, la convertimos en Promise.resolve()
    const promises = funcs.map(func => {
      const result = func(value, reset);
      return result instanceof Promise ? result : Promise.resolve();
    });

    // Espera a que todas terminen
    await Promise.all(promises);
  }

  getProfileState() {
    return new Promise<ProfileState>((resolve, reject) => {
      if(!this.profile) {
        this.profile = {} as ProfileState;
        this.profile$.subscribe(
          (profile) => {
            if(profile.id) {
              this.profile = profile;
              resolve(this.profile);
            }
          },
          (err) => {
            this.profile = null;
            reject(err);
          }
        );
      } else {
        resolve(this.profile);
      }
    });
  }

  getPersonDetails(force?: boolean) {
    const peopleApi = new PeopleApi(this.alfrescoApiService.getInstance());
    return new Promise<Person>((resolve, reject) => {
      if(!this.personDetails || force) {
        this.personDetails = new Person();
        peopleApi.getPerson('-me-').then((userInfo) => { 
          this.personDetails = userInfo?.entry;
          resolve(this.personDetails);
        }).catch((err) => {
          this.personDetails = null;
          reject(err);
        })
      } else {
        resolve(this.personDetails);
      }
    });
  }

  getStepsFiltered(steps:{ [id: string]: UDeuStep }): UDeuStep[] {
    return Object.values(steps).filter(step => !step.disabled);
  }

  updateStatus(uuid: string, udeuStatus: UdeuStatus): Promise<any> {
    const path = `smtecno/ssm/deusto/status/${uuid}`;
    const httpMethod = 'PUT';
    const pathParams = {};  // No funciona
    const queryParams = {};
    const headerParams = {};
    const formParams = {};
    const bodyParam = udeuStatus;
    const contentTypes = ['application/json'];
    const accepts = [];
    const returnType = '';
    const url = 'alfresco/service';

    return this.alfrescoApiService
    .getInstance()
    .contentClient.callApi(
      path,
      httpMethod,
      pathParams,
      queryParams,
      headerParams,
      formParams,
      bodyParam,
      contentTypes,
      accepts,
      returnType,
      url
    );
  }

  updateStatusMultiple(udeuStatusMultiple: UdeuStatusMultiple): Promise<any> {
    const path = `smtecno/ssm/deusto/status`;
    const httpMethod = 'PUT';
    const pathParams = {};  // No funciona
    const queryParams = {};
    const headerParams = {};
    const formParams = {};
    const bodyParam = udeuStatusMultiple;
    const contentTypes = ['application/json'];
    const accepts = [];
    const returnType = '';
    const url = 'alfresco/service';

    return this.alfrescoApiService
    .getInstance()
    .contentClient.callApi(
      path,
      httpMethod,
      pathParams,
      queryParams,
      headerParams,
      formParams,
      bodyParam,
      contentTypes,
      accepts,
      returnType,
      url
    );
  }

  getEmployeeData() : Promise<KeyValue<string,Empleado>> {
    return new Promise<KeyValue<string,Empleado>>((resolve, reject) => {
      this.getPersonDetails().then((personDetails) => {
        const email = personDetails.email?.trim() ?? personDetails.firstName;
        this.getGaudeDataListValues(EMPLEADO_DATALIST_NAME, email).then((res: GaudeEmpleado) => { 
          const employee = res.items ? res.items[0] : null;
          resolve({ key: email, value: employee});
        }).catch((err) => {
          reject(err);
        });
      }).catch((err) => {
          reject(err);
      });
    });
  }

  getFileByUuid(fileUUID: string): Promise<NodePaging> {
    const path = 'search';
    const httpMethod = 'POST';
    const pathParams = {};
    const queryParams = {};
    const headerParams = {};
    const formParams = {};
    const bodyParam = {
      query: {
        language: "afts",
        query: `${SYS_NODE_UUID_PROP}:'${fileUUID}'`
      },
      include: [
        "properties"
      ]
    };
    const contentTypes = [];
    const accepts = [];
    const returnType = '';
    const url = 'alfresco/api/-default-/public/search/versions/1';

    return this.alfrescoApiService
    .getInstance()
    .contentClient.callApi(
      path,
      httpMethod,
      pathParams,
      queryParams,
      headerParams,
      formParams,
      bodyParam,
      contentTypes,
      accepts,
      returnType,
      url
    );
  }

}
